// src/hooks/useCookieConsent.ts

'use client'

import { useState, useEffect, useCallback } from 'react'
import type { ConsentState, CategoryDefinition, CookieConsentConfig, ConsentRecord } from '../types'
import {
  initConsentManager,
  setConsent as setConsentCore,
  getConsent as getConsentCore,
  getAllConsent,
  revokeConsent as revokeConsentCore,
  onConsentChange,
  acceptAll as acceptAllCore,
  declineAll as declineAllCore,
  exportConsent as exportConsentCore,
} from '../core/consent-manager'
import { initScriptBlocking } from '../core/script-blocker'
import { getRemoteConfig } from '../core/config-fetcher'
import { detectLanguage } from '../utils/helpers'
import { DEFAULT_VERSION, DEFAULT_CATEGORIES } from '../constants/defaults'
import { initIntegrations, initConsentModeEarly } from '../integrations'

export interface UseCookieConsentReturn {
  // State
  consent: ConsentState
  hasConsent: boolean
  isReady: boolean

  // Categories
  categories: CategoryDefinition[]

  // Actions
  acceptAll: () => void
  declineAll: () => void
  setConsent: (choices: ConsentState) => void
  revokeConsent: () => void
  getConsent: (category?: string) => boolean

  // Utils
  exportConsent: () => ConsentRecord | null
}

export function useCookieConsent(
  userConfig: Partial<CookieConsentConfig> = {}
): UseCookieConsentReturn {
  const [consent, setConsentState] = useState<ConsentState>({})
  const [hasConsentState, setHasConsentState] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [categories, setCategories] = useState<CategoryDefinition[]>(DEFAULT_CATEGORIES)
  const [config, setConfig] = useState<CookieConsentConfig | null>(null)

  // Initialize
  useEffect(() => {
    let mounted = true

    async function init() {
      // CRITICAL: Initialize consent mode FIRST before anything else
      // This sets default consent to 'denied' for all parameters
      initConsentModeEarly()

      // Set config
      const finalConfig: CookieConsentConfig = {
        language: userConfig.language || detectLanguage(),
        accentColor: userConfig.accentColor || '#10b981',
        showSettingsButton: userConfig.showSettingsButton ?? true,
        closeButton: userConfig.closeButton ?? true,
        showRejectLink: userConfig.showRejectLink ?? true,
        mode: userConfig.mode || 'strict',
        domain: userConfig.domain || '',
        ttl: userConfig.ttl || 365,
        links: userConfig.links || {},
        customCategories: userConfig.customCategories || [],
        integrations: userConfig.integrations || {},
        apiUrl: userConfig.apiUrl,
        debug: userConfig.debug || false,
        onAccept: userConfig.onAccept,
        onDecline: userConfig.onDecline,
        onChange: userConfig.onChange,
        onReady: userConfig.onReady,
        onBlock: userConfig.onBlock,
      }

      setConfig(finalConfig)

      // Set debug flag
      if (finalConfig.debug) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ;(window as any).__MEUI_COOKIES_DEBUG__ = true
      }

      // Load remote config if API URL provided
      let remoteConfig = null
      if (finalConfig.apiUrl) {
        remoteConfig = await getRemoteConfig(finalConfig.apiUrl)
      }

      // Use remote categories if available
      const finalCategories = remoteConfig?.categories || DEFAULT_CATEGORIES
      if (mounted) {
        setCategories(finalCategories)
      }

      // Initialize script blocking
      initScriptBlocking()

      // Initialize consent manager
      const existingConsent = initConsentManager(
        finalCategories,
        remoteConfig?.version || DEFAULT_VERSION,
        finalConfig.ttl || 365
      )

      if (mounted) {
        if (existingConsent) {
          setConsentState(existingConsent)
          setHasConsentState(true)

          // Initialize integrations with existing consent
          // This will also call updateConsentMode() with existing consent
          if (finalConfig.integrations) {
            initIntegrations(finalConfig.integrations, existingConsent)
          }

          // Callback
          finalConfig.onReady?.()
        } else {
          // No consent found - explicitly set false
          setHasConsentState(false)
        }

        setIsReady(true)
      }
    }

    init()

    return () => {
      mounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Subscribe to consent changes
  useEffect(() => {
    const unsubscribe = onConsentChange(newConsent => {
      setConsentState(newConsent)
      setHasConsentState(Object.keys(newConsent).length > 0)

      // Callback
      config?.onChange?.(newConsent)
    })

    return unsubscribe
  }, [config])

  // Accept all
  const acceptAll = useCallback(() => {
    if (!config) return

    acceptAllCore(
      categories,
      DEFAULT_VERSION,
      config.language || 'en',
      config.ttl || 365,
      config.domain,
      config.onBlock
    )

    // Initialize integrations
    if (config.integrations) {
      const allConsent: ConsentState = {}
      categories.forEach(cat => {
        allConsent[cat.id] = true
      })
      initIntegrations(config.integrations, allConsent)
    }

    // Callback
    const allConsent = getAllConsent()
    config.onAccept?.(allConsent)
  }, [config, categories])

  // Decline all
  const declineAll = useCallback(() => {
    if (!config) return

    declineAllCore(
      categories,
      DEFAULT_VERSION,
      config.language || 'en',
      config.ttl || 365,
      config.domain,
      config.onBlock
    )

    // Callback
    config.onDecline?.()
  }, [config, categories])

  // Set consent
  const setConsent = useCallback(
    (choices: ConsentState) => {
      if (!config) return

      setConsentCore(
        choices,
        DEFAULT_VERSION,
        config.language || 'en',
        config.ttl || 365,
        config.domain,
        config.onBlock
      )

      // Initialize integrations
      if (config.integrations) {
        initIntegrations(config.integrations, choices)
      }

      // Callback
      config.onAccept?.(choices)
    },
    [config]
  )

  // Revoke consent
  const revokeConsent = useCallback(() => {
    if (!config) return

    revokeConsentCore(config.domain)
    setHasConsentState(false)
  }, [config])

  // Get consent
  const getConsent = useCallback((category?: string) => {
    return getConsentCore(category)
  }, [])

  // Export consent
  const exportConsent = useCallback(() => {
    return exportConsentCore()
  }, [])

  return {
    consent,
    hasConsent: hasConsentState,
    isReady,
    categories,
    acceptAll,
    declineAll,
    setConsent,
    revokeConsent,
    getConsent,
    exportConsent,
  }
}
